import { useState, useEffect } from "react";
import { Rocket, ArrowsClockwise, Clock } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import api from "../lib/api";

// Buffer-routable platforms. Order matters — this is the display order.
const BUFFER_PLATFORMS = [
    { key: "Threads",   label: "Threads",   hint: "text-first, no media required" },
    { key: "LinkedIn",  label: "LinkedIn",  hint: "text-first, no media required" },
    { key: "Facebook",  label: "Facebook",  hint: "text or media" },
    { key: "Twitter",   label: "Twitter/X", hint: "text-first, image optional" },
    { key: "Instagram", label: "Instagram", hint: "image or video REQUIRED" },
    { key: "TikTok",    label: "TikTok",    hint: "video REQUIRED" },
    { key: "YouTube",   label: "YouTube",   hint: "video REQUIRED" },
];

// Derive the primary platform from the idea's content_type. This is the
// only field pre-checked so a Threads idea doesn't accidentally blast
// every network.
function primaryPlatformFor(contentType) {
    const t = (contentType || "").toLowerCase();
    if (t.includes("threads"))    return "Threads";
    if (t.includes("linkedin"))   return "LinkedIn";
    if (t.includes("instagram"))  return "Instagram";
    if (t.includes("tiktok"))     return "TikTok";
    if (t.includes("youtube"))    return "YouTube";
    if (t.includes("tweet") || t.includes("twitter") || t.includes("thread")) return "Twitter";
    if (t.includes("facebook"))   return "Facebook";
    // "Podcast Topic", "Founder Video", "Ad Concept", "Carousel" → operator picks
    return null;
}

/**
 * Ship-to-Buffer platform selection dialog.
 *
 * Fixes the June-2026 issue where clicking Ship on a Threads-typed idea
 * over-generated Instagram/other variants and hit rate limits. Now the
 * operator sees a checklist with only the primary platform pre-selected;
 * they explicitly opt into any additional networks.
 *
 * onConfirm(platforms: string[]) receives the selected platform names
 * (or [] if the operator chose "All recommended"). An empty array signals
 * "no filter" so the backend ships everything the analyzer recommends
 * (the old behaviour, still available on demand).
 */
export default function ShipPlatformDialog({
    open,
    onOpenChange,
    onConfirm,
    contentType,          // string — idea's content_type
    title,                // string — idea title (shown in the dialog header)
    countLabel,           // string — e.g. "3 ideas" for bulk; falsy for single
    busy = false,
}) {
    const primary = primaryPlatformFor(contentType);
    const [selected, setSelected] = useState(() => new Set(primary ? [primary] : []));
    const [allRecommended, setAllRecommended] = useState(false);
    const [cooldowns, setCooldowns] = useState({}); // { instagram: iso_string }
    const [missing, setMissing] = useState([]);     // ["Threads", "LinkedIn", ...] — no channel_id set

    // Fetch cooldowns + configured-platforms in parallel on open. Platforms
    // that are (a) throttled OR (b) missing a Buffer channel_id are shown
    // disabled with a clear chip — the operator can't accidentally ship
    // to a platform that will fail at queue time.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        Promise.all([
            api.get("/content-studio/rate-limit-cooldowns").catch(() => ({ data: {} })),
            api.get("/content-studio/configured-platforms").catch(() => ({ data: {} })),
        ]).then(([cd, cfg]) => {
            if (cancelled) return;
            setCooldowns(cd.data?.cooldowns || {});
            setMissing(cfg.data?.missing || []);
        });
        return () => { cancelled = true; };
    }, [open]);

    // Re-derive primary whenever the dialog opens with a new idea. Skip
    // pre-selecting a primary that's throttled OR unconfigured — the
    // operator shouldn't open the dialog and see a dead default.
    useEffect(() => {
        if (open) {
            const p = primaryPlatformFor(contentType);
            const dead = p && (cooldowns[p.toLowerCase()] || missing.includes(p));
            setSelected(new Set(p && !dead ? [p] : []));
            setAllRecommended(false);
        }
    }, [open, contentType, cooldowns, missing]);

    const toggle = (k) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    };

    const handleConfirm = () => {
        // Empty array = no filter (ship all analyzer recommendations)
        onConfirm(allRecommended ? [] : Array.from(selected));
    };

    const nothingSelected = !allRecommended && selected.size === 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="ship-platform-dialog">
                <DialogHeader>
                    <DialogTitle className="tracking-tight">Ship to which platforms?</DialogTitle>
                    <DialogDescription className="text-sm">
                        {countLabel
                            ? <>Publishing <span className="font-mono">{countLabel}</span>. Each will be generated + queued to Buffer for every platform you tick below.</>
                            : <>Publishing <span className="italic">{title || "this idea"}</span>. Each ticked platform gets a fresh asset (text + branded image where relevant), fact-checked and queued to Buffer.</>}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    {BUFFER_PLATFORMS.map((p) => {
                        const cooldownIso = cooldowns[p.key.toLowerCase()];
                        const inCooldown = Boolean(cooldownIso);
                        const notConfigured = missing.includes(p.key);
                        const cooldownLabel = inCooldown
                            ? new Date(cooldownIso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                            : null;
                        const dead = inCooldown || notConfigured;
                        const checked = !dead && (allRecommended || selected.has(p.key));
                        const isPrimary = primary === p.key;
                        const disabled = dead || allRecommended || busy;
                        return (
                            <label
                                key={p.key}
                                data-testid={`ship-platform-${p.key.toLowerCase()}`}
                                className={[
                                    "flex items-start gap-3 rounded-sm border px-3 py-2 transition",
                                    disabled ? "cursor-not-allowed" : "cursor-pointer",
                                    checked
                                        ? "border-primary/60 bg-primary/5"
                                        : "border-border hover:border-muted-foreground/40",
                                    dead ? "opacity-60" : "",
                                    allRecommended && !dead ? "opacity-70" : "",
                                ].join(" ")}
                            >
                                <Checkbox
                                    checked={checked}
                                    disabled={disabled}
                                    onCheckedChange={() => toggle(p.key)}
                                    className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-sm font-semibold">{p.label}</span>
                                        {isPrimary && !dead && (
                                            <span className="text-[9px] font-mono uppercase tracking-widest text-primary/80 border border-primary/40 rounded-sm px-1 py-px">
                                                Primary
                                            </span>
                                        )}
                                        {inCooldown && (
                                            <span
                                                className="text-[9px] font-mono uppercase tracking-widest text-amber-700 border border-amber-500/50 bg-amber-500/10 rounded-sm px-1 py-px inline-flex items-center gap-1"
                                                data-testid={`ship-platform-cooldown-${p.key.toLowerCase()}`}
                                            >
                                                <Clock size={9} weight="bold" /> Cooldown · try {cooldownLabel}
                                            </span>
                                        )}
                                        {notConfigured && !inCooldown && (
                                            <span
                                                className="text-[9px] font-mono uppercase tracking-widest text-rose-700 border border-rose-500/50 bg-rose-500/10 rounded-sm px-1 py-px"
                                                data-testid={`ship-platform-unconfigured-${p.key.toLowerCase()}`}
                                            >
                                                Not configured
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] font-mono text-muted-foreground">
                                        {inCooldown
                                            ? `Buffer/Meta throttled this platform. Pick another network or come back at ${cooldownLabel}.`
                                            : notConfigured
                                            ? <>No Buffer channel ID set. <a href="/content-studio" onClick={(e) => { e.stopPropagation(); localStorage.setItem("haevn-cs-tab", "settings"); }} className="underline hover:text-primary">Configure in Settings</a>.</>
                                            : p.hint}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </div>

                <label className="flex items-center gap-2 border-t pt-3 mt-1 cursor-pointer"
                       data-testid="ship-all-recommended-toggle">
                    <Checkbox
                        checked={allRecommended}
                        disabled={busy}
                        onCheckedChange={(v) => setAllRecommended(Boolean(v))}
                    />
                    <span className="text-xs font-mono uppercase tracking-widest">
                        Ship everything the analyzer recommends
                    </span>
                </label>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        className="rounded-sm"
                        data-testid="ship-platform-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={busy || nothingSelected}
                        className="rounded-sm"
                        data-testid="ship-platform-confirm-btn"
                    >
                        {busy
                            ? <><ArrowsClockwise size={13} className="mr-1.5 animate-spin" /> Shipping…</>
                            : <><Rocket size={13} className="mr-1.5" /> {allRecommended ? "Ship all recommended" : `Ship to ${selected.size} platform${selected.size === 1 ? "" : "s"}`}</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
